import {AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {CommonModule} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {User} from '../api/interfaces/user.interface';
import {DefaultValues} from '@maporium-workspace/shared';
import {Subscription, switchMap} from 'rxjs';
import {UserService} from '../api/services/user.service';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {ColorInputComponent} from '../shared/forms/color-input/color-input.component';
import {TranslatePipe} from '@ngx-translate/core';

export interface UserProfileDialogData {
  focusOrganization?: boolean;
}

@Component({
  selector: 'maporium-profile-dialog',
  styles: [`
    .account-input {
      border: 1px solid rgb(118, 118, 118);
      border-radius: 4px;
      outline: none;
      background: #FFFFFF;
      padding: 2px 6px;
      font-size: 14px;
      font-family: inherit;
      height: 29px;
      box-sizing: border-box;
      width: 100%;
    }

    .account-input::placeholder {
      font-family: inherit;
      font-size: 14px;
      opacity: 1;
    }

    .account-input:focus {
      border-color: rgb(118, 118, 118);
    }

    textarea.account-input {
      height: auto;
      padding-top: 4px;
      resize: none;
      overflow: hidden;
      box-sizing: border-box;
    }

    .account-input.input-error {
      border-color: #cc0000;
    }

    .account-row {
      display: flex;
      align-items: flex-start;
      gap: 6px 8px;
      padding: 6px 0;
    }

    .row-icon {
      width: 30px;
      height: 29px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      color: #555;
    }

    .row-info {
      font-size: 13px;
      color: #666;
      min-width: 0;
      align-self: center;
    }

    .field-col {
      flex: 1;
      min-width: 0;
    }

    .field-error {
      color: #cc0000;
      font-size: 12px;
      margin-top: 2px;
    }

    app-color-input ::ng-deep .outer-box {
      height: 29px;
      box-sizing: border-box;
    }
  `],

  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0"><span class="material-symbols-rounded me-2">badge</span>
        {{ 'USER_PROFILE.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="close()"
            [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>

    <div mat-dialog-content>
      <form [formGroup]="form">
        <!-- Initiation -->
        <div class="account-row" *ngIf="smappingSince">
          <div class="row-icon">
            <i class="icon-smap-user-initiation"></i>
          </div>
          <div class="row-info">{{ 'USER_PROFILE.SMAPPING_SINCE' | translate:{ date: smappingSince } }}</div>
        </div>

        <!-- First / Last Name -->
        <div class="account-row">
          <div class="row-icon">
            <i class="icon-smap-account-individual"></i>
          </div>
          <div class="field-col">
            <input type="text" class="account-input" [placeholder]="'USER_PROFILE.FIRST_NAME_PLACEHOLDER' | translate"
                   formControlName="name"
                   [class.input-error]="showError('name')"/>
            <div class="field-error" *ngIf="showError('name')">{{ 'USER_PROFILE.ERROR_FIRST_NAME' | translate }}</div>
          </div>
          <div class="field-col">
            <input type="text" class="account-input" [placeholder]="'USER_PROFILE.LAST_NAME_PLACEHOLDER' | translate"
                   formControlName="lastName"
                   [class.input-error]="showError('lastName')"/>
            <div class="field-error" *ngIf="showError('lastName')">{{ 'USER_PROFILE.ERROR_LAST_NAME' | translate }}</div>
          </div>
        </div>

        <!-- Organization -->
        <div class="account-row">
          <div class="row-icon">
            <i class="icon-smap-account-enterprise"></i>
          </div>
          <div class="field-col">
            <input #organizationInput type="text" class="account-input" [placeholder]="'USER_PROFILE.ORGANIZATION_PLACEHOLDER' | translate"
                   formControlName="organization"
                   [class.input-error]="showError('organization')"/>
            <div class="field-error" *ngIf="showError('organization')">{{ 'USER_PROFILE.ERROR_ORGANIZATION' | translate }}</div>
          </div>
        </div>

        <!-- Biography -->
        <div class="account-row">
          <div class="row-icon">
            <i class="icon-smap-metadata-description"></i>
          </div>
          <div class="field-col">
            <textarea class="account-input"
                      [placeholder]="'USER_PROFILE.BIOGRAPHY_PLACEHOLDER' | translate"
                      formControlName="biography"
                      cdkTextareaAutosize
                      cdkAutosizeMinRows="3"
            ></textarea>
          </div>
        </div>

        <!-- Badge Color -->
        <div class="account-row">
          <div class="row-icon">
            <i class="icon-smap-object-style"></i>
          </div>
          <div class="field-col">
            <app-color-input [value]="selectedColor"
                             [showSmapColors]="false"
                             formControlName="color"></app-color-input>
          </div>
        </div>
      </form>
    </div>

    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" (click)="close()">{{ 'CREATE_MAP_DIALOG.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary" (click)="update()">{{ 'CREATE_MAP_DIALOG.UPDATE' | translate }}</button>
    </div>
  `,
  imports: [
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatInputModule,
    CommonModule,
    MatTooltipModule,
    CdkTextareaAutosize,
    ColorInputComponent,
    TranslatePipe,
  ],
  standalone: true,

})
export class UserProfileDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('organizationInput') organizationInputRef: ElementRef<HTMLInputElement> | undefined;

  currentUser: User | undefined;

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    organization: new FormControl(''),
    biography: new FormControl(''),
    color: new FormControl(DefaultValues.user.color)
  });

  selectedColor: string = DefaultValues.user.color;
  submitted = false;

  private subs = new Subscription();

  constructor(private matDialogRef: MatDialogRef<UserProfileDialogComponent>,
              private userService: UserService,
              @Optional() @Inject(MAT_DIALOG_DATA) private data: UserProfileDialogData | null) {
  }

  get smappingSince(): string {
    if (!this.currentUser?.createdAt) return '';
    return new Date(this.currentUser.createdAt)
      .toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'});
  }

  ngOnInit(): void {
    this.currentUser = this.userService.getCurrentUserFromStorage();

    if (this.currentUser) {
      this.patchFromUser(this.currentUser);

      // Stored user may be stale (no createdAt, outdated eduDiscount) — refresh it
      this.subs.add(this.userService.getOne(this.currentUser.id).subscribe(user => {
        this.currentUser = user;
        this.patchFromUser(user);
      }));

      this.subs.add(this.form.valueChanges.subscribe(() => {
        this.selectedColor = this.form.get('color')?.value as string;
      }));
    }
  }

  ngAfterViewInit(): void {
    if (this.data?.focusOrganization) {
      setTimeout(() => {
        const el = this.organizationInputRef?.nativeElement;
        el?.focus();
        el?.select();
      });
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  showError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return this.submitted && !!control?.invalid;
  }

  close() {
    this.matDialogRef.close();
  }

  update() {
    this.submitted = true;
    if (this.form.valid && this.currentUser) {
      const updatedUser = {
        ...this.currentUser,
        ...this.form.value,
        color: this.selectedColor
      }

      this.userService.update(updatedUser as User)
        .pipe(
          switchMap(() => this.userService.refreshLocalUser()),
        )
        .subscribe((user) => {
          this.matDialogRef.close();
        });
    }
  }

  private patchFromUser(user: User): void {
    this.form.patchValue({
      name: user.name,
      lastName: user.lastName,
      organization: user.organization,
      biography: user.biography,
      color: user.color
    });

    // Organization is only mandatory for users on the educational discount
    const orgControl = this.form.controls.organization;
    if (user.eduDiscount) {
      orgControl.addValidators(Validators.required);
    } else {
      orgControl.removeValidators(Validators.required);
    }
    orgControl.updateValueAndValidity({emitEvent: false});
  }
}
